import { useMemo, useState } from 'react';
import {
    RolloutEvent,
    RolloutEventKind,
    RolloutWatchView,
    SignalKind,
} from '../../api/rollout';
import {
    bucketize,
    clockSeconds,
    countOf,
    KIND_LABEL,
    RANGES,
    RangeKey,
} from './rolloutModel';

const WIDTH = 960;
const HEIGHT = 120;
const PAD = { top: 14, right: 12, bottom: 20, left: 36 };

const FLOW: { kind: SignalKind; className: string }[] = [
    { kind: 'Arrived', className: 'ro-series-1' },
    { kind: 'Succeeded', className: 'ro-series-2' },
];

// Bottom to top. Orange and yellow are too close to stand next to each other; violet sits between them.
const TROUBLE: { kind: SignalKind; className: string }[] = [
    { kind: 'Failed', className: 'ro-series-3' },
    { kind: 'RateLimited', className: 'ro-series-4' },
    { kind: 'Postponed', className: 'ro-series-5' },
];

/** The moments that get a mark on the timeline: what the apps did, and what went wrong. */
const MARKED: Partial<Record<RolloutEventKind, 'app' | 'bad'>> = {
    Started: 'app',
    Restarted: 'app',
    Stopped: 'bad',
    Silent: 'bad',
    Problem: 'bad',
    Telemetry: 'bad',
};

const clock = (ms: number) =>
    new Date(ms).toLocaleTimeString('nl-NL', {
        hour: '2-digit',
        minute: '2-digit',
    });

const niceMax = (value: number) => {
    if (value <= 4) return 4;

    const step = Math.pow(10, Math.floor(Math.log10(value)));

    return Math.ceil(value / step) * step;
};

/** The window a range shows: the live ranges end with the running column, the whole watch at its end. */
const windowOf = (
    range: RangeKey,
    view: RolloutWatchView,
    now: number,
    bucket: number,
    span: number
) => {
    const deployed = Date.parse(view.deployedAt);
    const ends = Date.parse(view.endsAt);

    if (range === 'window')
        return { from: deployed - 24 * 3600 * 1000, to: ends };

    const to = Math.min(
        Math.ceil((now + 1) / bucket) * bucket,
        Math.ceil(ends / bucket) * bucket
    );

    return { from: to - span, to };
};

/** About six labels along the axis, on round moments. */
const ticksOf = (
    range: RangeKey,
    from: number,
    to: number,
    deployed: number
) => {
    if (range === 'window')
        return [-24, -12, 0, 12, 24].map((h) => deployed + h * 3600 * 1000);

    const step = range === 'hour' ? 10 * 60 * 1000 : 3600 * 1000;
    const ticks: number[] = [];

    for (let t = Math.ceil(from / step) * step; t <= to; t += step)
        ticks.push(t);

    return ticks;
};

/**
 * What came in and went through, and below it what failed, waited or hit Exact's limit. The
 * running column fills while you watch; app restarts and new failures are marks on top. One
 * axis per chart.
 */
export const RolloutCharts = ({
    view,
    now,
    range,
}: {
    view: RolloutWatchView;
    now: number;
    range: RangeKey;
}) => {
    const { bucket, span } = RANGES.find((r) => r.key === range) ?? RANGES[0];
    const deployed = Date.parse(view.deployedAt);
    const { from, to } = windowOf(range, view, now, bucket, span);
    const buckets = useMemo(
        () => bucketize(view.timeline, from, to, bucket),
        [view.timeline, from, to, bucket]
    );
    const [hover, setHover] = useState<number>();
    const [hoverAt, setHoverAt] = useState(0);

    const innerWidth = WIDTH - PAD.left - PAD.right;
    const innerHeight = HEIGHT - PAD.top - PAD.bottom;
    const x = (ms: number) =>
        PAD.left + ((ms - from) / (to - from)) * innerWidth;
    const columnWidth = Math.max(1, (bucket / (to - from)) * innerWidth - 1);

    const flowMax = niceMax(
        Math.max(
            ...buckets.map((b) =>
                Math.max(
                    countOf(b.counts, 'Arrived'),
                    countOf(b.counts, 'Succeeded')
                )
            )
        )
    );
    const troubleMax = niceMax(
        Math.max(
            ...buckets.map((b) =>
                TROUBLE.reduce((sum, t) => sum + countOf(b.counts, t.kind), 0)
            )
        )
    );

    const y = (value: number, max: number) =>
        PAD.top + innerHeight - (value / max) * innerHeight;

    const current = buckets.findIndex(
        (b) => b.start <= now && now < b.start + bucket
    );
    const marks = view.events.filter(
        (e) =>
            MARKED[e.kind] && Date.parse(e.at) >= from && Date.parse(e.at) <= to
    );

    const onMove = (event: React.MouseEvent<SVGSVGElement>) => {
        const box = event.currentTarget.getBoundingClientRect();
        const svgX = ((event.clientX - box.left) / box.width) * WIDTH;
        const ms = from + ((svgX - PAD.left) / innerWidth) * (to - from);
        const index = Math.floor((ms - buckets[0].start) / bucket);

        setHover(index >= 0 && index < buckets.length ? index : undefined);
        setHoverAt((event.clientX - box.left) / box.width);
    };

    const ticks = ticksOf(range, from, to, deployed);
    const live = current >= 0 ? buckets[current] : undefined;
    const hovered = hover !== undefined ? buckets[hover] : undefined;
    const marksIn = (start: number): RolloutEvent[] =>
        marks.filter(
            (e) =>
                Date.parse(e.at) >= start && Date.parse(e.at) < start + bucket
        );

    const counts = (b: (typeof buckets)[number]) =>
        [...FLOW, ...TROUBLE].map((k) => (
            <span key={k.kind} className="ro-focus__item">
                <span className={`ro-legend__swatch ${k.className}`} />
                {KIND_LABEL[k.kind]} <b>{countOf(b.counts, k.kind)}</b>
            </span>
        ));

    const markers = (max: number) => (
        <>
            {current >= 0 && (
                <rect
                    className="ro-chart__current"
                    x={x(buckets[current].start)}
                    y={PAD.top}
                    width={columnWidth}
                    height={innerHeight}
                />
            )}
            <line
                className="ro-chart__grid"
                x1={PAD.left}
                x2={WIDTH - PAD.right}
                y1={y(0, max)}
                y2={y(0, max)}
            />
            <line
                className="ro-chart__grid"
                x1={PAD.left}
                x2={WIDTH - PAD.right}
                y1={y(max, max)}
                y2={y(max, max)}
            />
            <text
                className="ro-chart__axis"
                x={PAD.left - 6}
                y={y(max, max) + 4}
                textAnchor="end"
            >
                {max}
            </text>
            <text
                className="ro-chart__axis"
                x={PAD.left - 6}
                y={y(0, max) + 4}
                textAnchor="end"
            >
                0
            </text>
            {deployed >= from && deployed <= to && (
                <line
                    className="ro-chart__deploy"
                    x1={x(deployed)}
                    x2={x(deployed)}
                    y1={PAD.top}
                    y2={HEIGHT - PAD.bottom}
                />
            )}
            {now >= from && now < to && (
                <line
                    className="ro-chart__now"
                    x1={x(now)}
                    x2={x(now)}
                    y1={PAD.top}
                    y2={HEIGHT - PAD.bottom}
                />
            )}
            {marks.map((e) => (
                <line
                    key={`${e.at}-${e.kind}-${e.subject}`}
                    className={`ro-chart__mark is-${MARKED[e.kind]}`}
                    x1={x(Date.parse(e.at))}
                    x2={x(Date.parse(e.at))}
                    y1={PAD.top - 10}
                    y2={HEIGHT - PAD.bottom}
                />
            ))}
            {ticks.map((tick) => (
                <text
                    key={tick}
                    className="ro-chart__axis"
                    x={x(tick)}
                    y={HEIGHT - 4}
                    // Labels at the edges stay inside the chart.
                    textAnchor={
                        x(tick) > WIDTH - PAD.right - 20
                            ? 'end'
                            : x(tick) < PAD.left + 20
                            ? 'start'
                            : 'middle'
                    }
                >
                    {range === 'window' && tick === deployed
                        ? 'deploy'
                        : clock(tick)}
                </text>
            ))}
            {hover !== undefined && (
                <rect
                    className="ro-chart__hover"
                    x={x(buckets[hover].start)}
                    y={PAD.top}
                    width={columnWidth}
                    height={innerHeight}
                />
            )}
        </>
    );

    const line = (kind: SignalKind) =>
        buckets
            .filter((b) => b.start <= now)
            .map(
                (b, i) =>
                    `${i === 0 ? 'M' : 'L'}${(
                        x(b.start) +
                        columnWidth / 2
                    ).toFixed(1)},${y(countOf(b.counts, kind), flowMax).toFixed(
                        1
                    )}`
            )
            .join(' ');

    return (
        <div className="ro-charts" onMouseLeave={() => setHover(undefined)}>
            {live && (
                <div className="ro-focus is-live" role="status">
                    <span className="ro-focus__when">
                        <span className="ro-focus__dot" aria-hidden />
                        Nu {clock(live.start)}–{clock(live.start + bucket)}
                    </span>
                    {counts(live)}
                </div>
            )}
            {hovered && (
                <div
                    className={`ro-tip${hoverAt > 0.6 ? ' is-left' : ''}`}
                    style={{ left: `${hoverAt * 100}%` }}
                    role="tooltip"
                >
                    <strong>
                        {clock(hovered.start)}–{clock(hovered.start + bucket)}
                        {hover === current ? ' · loopt' : ''}
                    </strong>
                    {counts(hovered)}
                    {marksIn(hovered.start).map((e) => (
                        <span
                            key={`${e.at}-${e.kind}-${e.subject}`}
                            className={`ro-tip__mark is-${MARKED[e.kind]}`}
                        >
                            {clockSeconds(e.at)}{' '}
                            {e.kind === 'Problem' || e.kind === 'Telemetry'
                                ? e.text
                                : `${e.subject} ${e.text}`}
                        </span>
                    ))}
                </div>
            )}
            <figure className="ro-chart">
                <figcaption className="ro-chart__title">
                    Doorstroom
                    <span className="ro-legend">
                        {FLOW.map((f) => (
                            <span key={f.kind} className="ro-legend__item">
                                <span
                                    className={`ro-legend__swatch ${f.className}`}
                                />
                                {KIND_LABEL[f.kind]}
                            </span>
                        ))}
                        <span className="ro-legend__item">
                            <span className="ro-legend__mark is-app" />
                            app gestart
                        </span>
                        <span className="ro-legend__item">
                            <span className="ro-legend__mark is-bad" />
                            nieuwe fout of app gestopt
                        </span>
                    </span>
                </figcaption>
                <svg
                    viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                    role="img"
                    aria-label="Aangekomen en geslaagd"
                    onMouseMove={onMove}
                >
                    {markers(flowMax)}
                    {FLOW.map((f) => (
                        <path
                            key={f.kind}
                            className={`ro-chart__line ${f.className}`}
                            d={line(f.kind)}
                        />
                    ))}
                </svg>
            </figure>
            <figure className="ro-chart">
                <figcaption className="ro-chart__title">
                    Problemen
                    <span className="ro-legend">
                        {TROUBLE.map((t) => (
                            <span key={t.kind} className="ro-legend__item">
                                <span
                                    className={`ro-legend__swatch ${t.className}`}
                                />
                                {KIND_LABEL[t.kind]}
                            </span>
                        ))}
                    </span>
                </figcaption>
                <svg
                    viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                    role="img"
                    aria-label="Mislukte pogingen, Exact-limiet en wacht"
                    onMouseMove={onMove}
                >
                    {markers(troubleMax)}
                    {buckets.map((b) => {
                        let base = 0;

                        return TROUBLE.map((t) => {
                            const value = countOf(b.counts, t.kind);

                            if (value === 0) return null;

                            const top = y(base + value, troubleMax);
                            const bottom = y(base, troubleMax);
                            base += value;

                            return (
                                <rect
                                    key={`${b.start}-${t.kind}`}
                                    className={`ro-chart__bar ${t.className}`}
                                    x={x(b.start)}
                                    y={top}
                                    width={columnWidth}
                                    // A 2px gap between stacked segments, so the colours never touch.
                                    height={Math.max(1, bottom - top - 2)}
                                />
                            );
                        });
                    })}
                </svg>
            </figure>
        </div>
    );
};
